import { renderPromptToHtml } from '../lib/markdown';

export interface PublicQuestionOption {
  id: string;
  label?: string;
  stack?: string;
  output?: string;
}

export interface PublicQuestion {
  id: string;
  category: string;
  prompt: string;
  kind: 'single' | 'multi';
  options: PublicQuestionOption[];
  difficulty?: 1 | 2 | 3;
}

interface Props {
  question: PublicQuestion;
  selected: string[];
  onChange: (selected: string[]) => void;
  index: number;
  total: number;
  /** When set, options show ✓/✗ next to them and are disabled. */
  reveal?: { correctIds: string[]; correct: boolean } | null;
}

export function QuestionView({ question, selected, onChange, index, total, reveal }: Props) {
  const toggle = (id: string) => {
    if (reveal) return;
    if (question.kind === 'single') {
      onChange([id]);
    } else {
      onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
    }
  };
  const isStructured = question.options.some(
    (o) => o.stack !== undefined || o.output !== undefined
  );
  return (
    <div className="question">
      <div className="question-head">
        <span className="question-counter">
          Question {index + 1} / {total}
        </span>
        <span className={`question-cat cat-${question.category}`}>{question.category}</span>
      </div>
      <div
        className="question-prompt"
        dangerouslySetInnerHTML={{ __html: renderPromptToHtml(question.prompt) }}
      />
      <p className="question-instruction">
        {question.kind === 'single' ? 'Pick ONE option' : 'Pick ONE OR MORE options'}
      </p>
      {isStructured ? (
        <table className="options-table">
          <thead>
            <tr>
              <th></th>
              <th>Resulting stack</th>
              <th>Output</th>
            </tr>
          </thead>
          <tbody>
            {question.options.map((opt) => {
              const checked = selected.includes(opt.id);
              const isCorrectChoice = reveal?.correctIds.includes(opt.id) ?? false;
              const isUserChoice = checked;
              const cls = [
                'option-row',
                checked ? 'option-checked' : '',
                reveal && isCorrectChoice ? 'option-correct' : '',
                reveal && isUserChoice && !isCorrectChoice ? 'option-wrong' : ''
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <tr
                  key={opt.id}
                  className={cls}
                  onClick={() => toggle(opt.id)}
                >
                  <td className="option-radio">
                    <input
                      type={question.kind === 'single' ? 'radio' : 'checkbox'}
                      name={`q-${question.id}`}
                      checked={checked}
                      readOnly
                      disabled={!!reveal}
                    />
                    {reveal && isCorrectChoice && <span className="mark mark-ok"> ✓</span>}
                    {reveal && isUserChoice && !isCorrectChoice && (
                      <span className="mark mark-bad"> ✗</span>
                    )}
                  </td>
                  <td><code>{opt.stack ?? ''}</code></td>
                  <td><code>{opt.output ?? ''}</code></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <ul className="options">
          {question.options.map((opt) => {
            const checked = selected.includes(opt.id);
            const isCorrectChoice = reveal?.correctIds.includes(opt.id) ?? false;
            const isUserChoice = checked;
            const cls = [
              'option',
              checked ? 'option-checked' : '',
              reveal && isCorrectChoice ? 'option-correct' : '',
              reveal && isUserChoice && !isCorrectChoice ? 'option-wrong' : ''
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <li key={opt.id}>
                <label className={cls}>
                  <input
                    type={question.kind === 'single' ? 'radio' : 'checkbox'}
                    name={`q-${question.id}`}
                    checked={checked}
                    onChange={() => toggle(opt.id)}
                    disabled={!!reveal}
                  />
                  <code>{opt.label ?? ''}</code>
                  {reveal && isCorrectChoice && <span className="mark mark-ok"> ✓</span>}
                  {reveal && isUserChoice && !isCorrectChoice && (
                    <span className="mark mark-bad"> ✗</span>
                  )}
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
